import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";

import { availableLanguages } from "~/i18n/languages";

import styled from "styled-components";

import { Topic, TopicEntity } from "~/types";
import { AppContext } from "~/contexts/AppContext";

const LanguageSelectorStyle = styled.div`
    position: absolute;
    left: 20px;
    color: white;
    margin-top: 7px;

    background-color: #00000055;
    padding: 7px 14px;
    border-radius: 20px;

    select {
        background-color: transparent;
        border: none;
        color: transparent;
        font-size: 16px;
        cursor: pointer;
        outline: none;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        padding-right: 20px;
        background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: right center;
        background-size: 16px;
    }

    select option {
        color: black;
    }
`;

interface LanguageSelectorProps {
    topic: Topic | null;
    entity: TopicEntity | null;
    languages: Array<{ code: string; label: string; flag: string, displayName: string }>;
    onLanguageChange?: () => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = (props) => {
    const { i18n } = useTranslation();
    const { recordAnalyticsEvent } = useContext(AppContext)!;
    const [isOpen, setIsOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const currentLanguage = i18n.language;
    const currentLang = props.languages.find(lang => lang.code === currentLanguage) || props.languages[0];

    // Detect Chrome/Edge on Windows
    const isWindows = navigator.platform.indexOf('Win') > -1;
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(navigator.userAgent);
    const shouldHideFlags = isWindows && (isChrome || isEdge);

    useEffect(() => {
        recordAnalyticsEvent(
            'language-selector-ab-test-2025-11',
            'open-language-selector-OLD',
            props.topic?.id,
            props.entity?.id
        );
    }, [props.topic]);

    useEffect(() => {
        const checkIsMobile = () => {
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                                  ('ontouchstart' in window) ||
                                  (window.innerWidth <= 768);
            setIsMobile(isMobileDevice);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const handleLanguageChange = (langCode: string) => {
        i18n.changeLanguage(langCode);
        setIsOpen(false);
        if (props.onLanguageChange) {
            props.onLanguageChange();
        }
    };

    const handleNativeSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        handleLanguageChange(e.target.value);
    };

    // Close dropdown when clicking outside (only for desktop)
    useEffect(() => {
        if (isMobile) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, isMobile]);

    // Mobile native select
    if (isMobile) {
        return (
            <LanguageSelectorStyle>
                <div style={{ position: 'relative', display: 'inline-block', width: 'auto' }}>
                    <select
                        value={currentLanguage}
                        onChange={handleNativeSelectChange}
                        style={{
                            width: shouldHideFlags ? '45px' : '65px'
                        }}
                    >
                        {props.languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {shouldHideFlags ? lang.displayName : `${lang.flag} ${lang.displayName}`}
                            </option>
                        ))}
                    </select>
                    <div style={{
                        position: 'absolute',
                        left: '0',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: 'white',
                        fontSize: '16px',
                    }}>
                        {shouldHideFlags ? currentLang.label : `${currentLang.flag} ${currentLang.label}`}
                    </div>
                </div>
            </LanguageSelectorStyle>
        );
    }

    // Desktop custom dropdown
    return (
        <LanguageSelectorStyle>
            <div 
                ref={dropdownRef}
                style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    position: 'relative'
                }}
                onClick={() => setIsOpen(!isOpen)}
            >
                {shouldHideFlags ? currentLang.label : `${currentLang.flag} ${currentLang.label}`} <FontAwesomeIcon icon={faChevronDown} />
                
                {isOpen && (
                    <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '8px',
                        padding: '8px 0',
                        minWidth: '120px',
                        marginTop: '4px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000
                    }}>
                        {props.languages.map((lang) => (
                            <div
                                key={lang.code}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    color: '#333',
                                    fontSize: '14px',
                                    backgroundColor: currentLanguage === lang.code ? 'rgba(0, 0, 0, 0.1)' : 'transparent'
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleLanguageChange(lang.code);
                                }}
                                onMouseEnter={(e) => {
                                    if (currentLanguage !== lang.code) {
                                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (currentLanguage !== lang.code) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                 {shouldHideFlags ? lang.displayName : `${lang.flag} ${lang.displayName}`}
                             </div>
                        ))}
                    </div>
                )}
            </div>
        </LanguageSelectorStyle>
    );
};

interface LanguageSelectorWrapperProps {
    topic: Topic | null;
    entity: TopicEntity | null;
    altLngs: string[];
    onLanguageChange?: () => void;
}

const LanguageSelectorWrapper: React.FC<LanguageSelectorWrapperProps> = (props) => {

    if(props.topic === null) {
        return null;
    }

    const defaultLanguage = props.topic.defaultLanguage;

    const languages = [defaultLanguage, ...props.altLngs].map(lang => availableLanguages[lang as keyof typeof availableLanguages]);

    if(languages.length < 2) {
        return null;
    }

    return (
        <LanguageSelector
            topic={props.topic}
            entity={props.entity}
            languages={languages}
            onLanguageChange={props.onLanguageChange}
        />
    )
};

export default LanguageSelectorWrapper;